/**
 * TriggerRunDrawer — confirmation dialog for performance_refresh / scheduled_rebuild
 * / creative_batch, full configuration drawer for compass_directed.
 *
 * Always sets trigger_source=manual + trigger_id=admin-ui — these are required
 * by the Phase 4 endpoint.
 */
import { useState } from 'react';
import { Loader2, Settings2, X } from 'lucide-react';
import { gardenApi, type GardenRunType } from '@/services/api';
import { useAppStore } from '@/stores/app';
import { ConfirmDialog } from '@/components/pdo/ConfirmDialog';

interface Props {
  clientUid: string;
  runType: GardenRunType;
  onCancel: () => void;
  onSuccess: (runId: string) => void;
}

export function TriggerRunDrawer({
  clientUid,
  runType,
  onCancel,
  onSuccess,
}: Props) {
  const addNotification = useAppStore((s) => s.addNotification);
  const [busy, setBusy] = useState(false);
  const [stageJson, setStageJson] = useState('{}');
  const [acceptJson, setAcceptJson] = useState('{}');
  const [baseVersion, setBaseVersion] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const trigger = async (
    overrides?: Record<string, unknown>,
    accept?: Record<string, unknown>,
    base?: string,
  ) => {
    setBusy(true);
    try {
      const res = await gardenApi.triggerRun(clientUid, {
        run_type: runType,
        trigger_source: 'manual',
        trigger_id: 'admin-ui',
        base_version_id: base || undefined,
        stage_config_overrides: overrides,
        acceptance_criteria_overrides: accept,
      });
      addNotification('success', `Triggered ${runType} → ${res.run_id}`);
      onSuccess(res.run_id);
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  if (runType === 'compass_directed') {
    const submit = async () => {
      let stage: Record<string, unknown> | undefined;
      let accept: Record<string, unknown> | undefined;
      setJsonError(null);
      try {
        stage = stageJson.trim() ? JSON.parse(stageJson) : undefined;
        accept = acceptJson.trim() ? JSON.parse(acceptJson) : undefined;
      } catch (e) {
        setJsonError(e instanceof Error ? e.message : String(e));
        return;
      }
      await trigger(stage, accept, baseVersion.trim() || undefined);
    };

    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-end"
        onClick={() => !busy && onCancel()}
      >
        <div className="flex-1 bg-black/30 backdrop-blur-sm" />
        <div
          className="w-full max-w-lg bg-white shadow-2xl flex flex-col h-full animate-slide-in-right"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex items-start justify-between gap-2 px-5 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Settings2 size={16} className="text-purple-600" />
              <div>
                <h3 className="text-base font-semibold text-navy-900">
                  Compass-directed run
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Override stage config or acceptance criteria for this cycle.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Base version id (optional)
              </label>
              <input
                type="text"
                value={baseVersion}
                onChange={(e) => setBaseVersion(e.target.value)}
                placeholder="leave blank to start from current"
                className="w-full px-2.5 py-1.5 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Stage config overrides (JSON)
              </label>
              <textarea
                rows={8}
                value={stageJson}
                onChange={(e) => setStageJson(e.target.value)}
                spellCheck={false}
                className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Acceptance criteria overrides (JSON)
              </label>
              <textarea
                rows={8}
                value={acceptJson}
                onChange={(e) => setAcceptJson(e.target.value)}
                spellCheck={false}
                className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
            {jsonError && (
              <p className="text-xs text-red-600 font-mono">JSON error: {jsonError}</p>
            )}
          </div>
          <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40"
            >
              {busy && <Loader2 size={13} className="animate-spin" />}
              Trigger Run
            </button>
          </footer>
        </div>
      </div>
    );
  }

  const labels: Record<GardenRunType, string> = {
    performance_refresh: 'Trigger performance refresh',
    scheduled_rebuild: 'Trigger scheduled rebuild',
    creative_batch: 'Trigger creative batch',
    compass_directed: '',
    creative_memory_cycle: 'Trigger memory cycle',
    strategic_rebuild: 'Trigger strategic rebuild',
    creative_review_backfill: 'Trigger creative review backfill',
    creative_review_refresh: 'Trigger creative review refresh',
    creative_review_seed: 'Trigger creative review seed',
    strategy_docket_refresh: 'Rebuild the creative strategy docket',
  };

  return (
    <ConfirmDialog
      open
      title={labels[runType] || 'Trigger run'}
      message={
        <p>
          Run <span className="font-mono font-semibold">{runType}</span> for{' '}
          <span className="font-mono font-semibold">{clientUid}</span>?
        </p>
      }
      confirmLabel="Trigger"
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => trigger()}
    />
  );
}
